import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Fingerprint } from 'lucide-react';
import { completePasswordReset } from '../../utils/firebase';
import { sanitizeAuthError } from '../../utils/userFacingErrors';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const recovery = useMemo(
    () => ({
      userId: params.get('userId') || '',
      secret: params.get('secret') || '',
    }),
    [params],
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await completePasswordReset(
        password,
        recovery.userId && recovery.secret ? recovery : undefined,
      );
      navigate('/login', { replace: true });
    } catch (err) {
      setError(sanitizeAuthError(err instanceof Error ? err.message : 'Could not update password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 text-white">
      <div className="grain-panel w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-secondary">
            <Fingerprint className="h-6 w-6" />
          </div>
          <h2 className="text-2xl font-bold">New password</h2>
          <p className="mt-2 text-sm text-white/45">Choose a password for your studio account.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block pl-1 text-[10px] font-bold uppercase tracking-widest text-white/40">
              New password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/20" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-10 pr-4 font-mono text-sm text-white outline-none focus:border-secondary"
                minLength={6}
                required
              />
            </div>
          </div>
          <div>
            <label className="mb-2 block pl-1 text-[10px] font-bold uppercase tracking-widest text-white/40">
              Confirm
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/20" />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-10 pr-4 font-mono text-sm text-white outline-none focus:border-secondary"
                minLength={6}
                required
              />
            </div>
          </div>
          {error && (
            <p className="rounded border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-300">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-white py-4 text-xs font-bold uppercase tracking-widest text-black hover:bg-secondary disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Update password'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-white/35">
          <Link to="/login" className="text-secondary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
